import type { FC } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/hooks/useTranslation';

export type WearNextField = 'fz' | 'ae' | 'volumePerPiece' | 'plannedPieces';

export type WearNextValues = Record<WearNextField, string>;

interface WearNextOperationProps {
  values: WearNextValues;
  onChange: (field: WearNextField, value: string) => void;
  /** The tool's last trace gave defaults for fz, ae or the volume per piece. */
  hasTraceDefaults: boolean;
  edited: boolean;
  onBackToTrace: () => void;
  disabled: boolean;
}

const FIELDS: { field: WearNextField; label: string; unit: string; step: number }[] = [
  { field: 'fz', label: 'fz', unit: 'mlWear.context.units.mmPerTooth', step: 0.001 },
  { field: 'ae', label: 'ae', unit: 'mlWear.context.units.mm', step: 0.01 },
  {
    field: 'volumePerPiece',
    label: 'mlWear.context.volumePerPiece',
    unit: 'mlWear.context.units.cm3',
    step: 0.005,
  },
  {
    field: 'plannedPieces',
    label: 'mlWear.context.plannedPieces',
    unit: 'mlWear.context.units.pieces',
    step: 1,
  },
];

/** Step 2: settings of the next operation (optional), pre-filled from the last trace. */
export const WearNextOperation: FC<WearNextOperationProps> = ({
  values,
  onChange,
  hasTraceDefaults,
  edited,
  onBackToTrace,
  disabled,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2" data-testid="wear-next">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-2xs font-semibold text-primary-foreground">
          2
        </span>
        <span className="text-sm font-medium text-foreground">{t('mlWear.context.next')}</span>
        <span className="text-2xs text-muted-foreground">{t('mlWear.context.optional')}</span>
        {edited ? (
          <Badge size="sm" caps>
            {t('mlWear.context.edited')}
          </Badge>
        ) : (
          hasTraceDefaults && (
            <Badge tone="primary" size="sm" caps>
              {t('mlWear.context.fromTrace')}
            </Badge>
          )
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map(({ field, label, unit, step }) => (
          <label key={field} className="flex min-w-0 flex-col gap-1">
            <span className="flex justify-between gap-1 text-xs text-muted-foreground">
              <span className="truncate">{label.includes('.') ? t(label) : label}</span>
              <span className="shrink-0 font-mono text-2xs">{t(unit)}</span>
            </span>
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              step={step}
              data-testid={`wear-next-${field}`}
              value={values[field]}
              placeholder="—"
              disabled={disabled}
              onChange={(event) => onChange(field, event.target.value)}
              className="px-2 tabular-nums"
            />
          </label>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {hasTraceDefaults && `${t('mlWear.context.nextHelpFromTrace')} `}
        {t('mlWear.context.nextHelpIgnored')}
        {edited && hasTraceDefaults && (
          <>
            {' '}
            <Button
              variant="link"
              size="inline"
              className="text-xs"
              data-testid="wear-back-to-trace"
              onClick={onBackToTrace}
            >
              {t('mlWear.context.backToTrace')}
            </Button>
          </>
        )}
      </p>
    </div>
  );
};
